import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProjectKnowledgeConnections, ProjectKnowledgeUrls } from './ProjectKnowledgeSources.tsx';
import DropArea from '../../core/components/DropArea.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import FormField from '../../core/components/FormField.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import {
    DefaultChannelsConfig,
    SharePointToolProviderConfig,
    SftpKnowledgeSourceConfig,
} from '../../products/assistants/components/config/ConfigSteps.tsx';
import InstructionSections, {
    InstructionSection,
} from '../../products/assistants/components/knowledge/InstructionSections.tsx';
import KnowledgeFileList from '../../products/assistants/components/knowledge/KnowledgeFileList.tsx';
import { KNOWLEDGE_FILE_ACCEPT, KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL } from '../../products/assistants/constants.ts';

export interface ProjectFormValues {
    name: string;
    description: string;
    instructions: InstructionSection[];
    // persisted knowledge files as they come from the API plus freshly dropped File objects (agent config pattern)
    files: any[];
    // synced sources, the same shapes as the agent form
    sharepointKnowledgeSource: SharePointToolProviderConfig | null;
    sftpKnowledgeSource: SftpKnowledgeSourceConfig | null;
    knowledgeUrls: string[];
    // tool enablement (ENG-11388), the same shapes as the agent form
    erpToolProvider: { key: string; integrationId?: string | null } | null;
    orderHistoryToolProvider: { enabled: boolean } | null;
    attachmentsEnabled: boolean;
    defaultChannels: DefaultChannelsConfig | null;
    // who the project is shared with, as picker values (see RecipientsField)
    recipients: string[];
}

export const PROJECT_FORM_DEFAULTS: ProjectFormValues = {
    name: '',
    description: '',
    instructions: [],
    files: [],
    sharepointKnowledgeSource: null,
    sftpKnowledgeSource: null,
    knowledgeUrls: [],
    erpToolProvider: null,
    orderHistoryToolProvider: null,
    attachmentsEnabled: false,
    defaultChannels: null,
    recipients: [],
};

/** The query adds `__typename` to persisted sections, which the mutation input rejects — only label and body go back. */
export const toInstructionInputs = (sections: InstructionSection[]) =>
    (sections || []).map(({ label, body }) => ({ label, body }));

/** Mutation variables for the knowledge sources, mapped like the agent form. SharePoint and SFTP are left out
 *  (not null: absent means "unchanged" to the backend) when the user may not configure them. */
export const toKnowledgeSourceInputs = (values: ProjectFormValues, { includeSynced }: { includeSynced: boolean }) => ({
    knowledgeUrls: (values.knowledgeUrls || []).map((url) => url.trim()).filter(Boolean),
    ...(includeSynced
        ? {
              sharepointKnowledgeSource: values.sharepointKnowledgeSource
                  ? {
                        integrationId: values.sharepointKnowledgeSource.integrationId,
                        siteId: values.sharepointKnowledgeSource.siteId,
                        driveId: values.sharepointKnowledgeSource.driveId,
                        selectedItems: (values.sharepointKnowledgeSource.selectedItems || []).map(({ id, path }) => ({
                            id,
                            path,
                        })),
                        integrationName: values.sharepointKnowledgeSource.integrationName || null,
                        siteName: values.sharepointKnowledgeSource.siteName || null,
                        driveName: values.sharepointKnowledgeSource.driveName || null,
                    }
                  : null,
              sftpKnowledgeSource: values.sftpKnowledgeSource
                  ? { sftpConfigId: values.sftpKnowledgeSource.sftpConfigId, path: values.sftpKnowledgeSource.path }
                  : null,
          }
        : {}),
});

const DEFAULT_CHANNEL_KEYS = [
    'orderChannelId',
    'orderConfirmationChannelId',
    'rfqChannelId',
    'invoiceChannelId',
    'deliveryNoteChannelId',
] as const;

/** Form values for the project's tool enablement, as the query returns them. */
export const toToolConfigValues = (project: any) => ({
    erpToolProvider: project.erpToolProvider
        ? { key: project.erpToolProvider.key, integrationId: project.erpToolProvider.integrationId || null }
        : null,
    orderHistoryToolProvider: project.orderHistoryToolProvider ? { enabled: true } : null,
    attachmentsEnabled: Boolean(project.attachmentsEnabled),
    defaultChannels: project.defaultChannels
        ? (Object.fromEntries(
              DEFAULT_CHANNEL_KEYS.map((key) => [key, project.defaultChannels[key] || ''])
          ) as DefaultChannelsConfig)
        : null,
});

/** Mutation variables for the tool enablement, mapped like the agent form: an empty default-channel card is
 *  saved as none. ERP and order history are left out (absent means "unchanged") when the user may not set them. */
export const toToolConfigInputs = (values: ProjectFormValues, { includeProviders }: { includeProviders: boolean }) => {
    const channels = values.defaultChannels;
    const hasChannel = channels && DEFAULT_CHANNEL_KEYS.some((key) => channels[key]);
    return {
        attachmentsEnabled: values.attachmentsEnabled,
        defaultChannels: hasChannel
            ? Object.fromEntries(DEFAULT_CHANNEL_KEYS.map((key) => [key, channels[key] || null]))
            : null,
        ...(includeProviders
            ? {
                  erpToolProvider: values.erpToolProvider
                      ? { key: values.erpToolProvider.key, integrationId: values.erpToolProvider.integrationId || null }
                      : null,
                  orderHistoryToolProvider: values.orderHistoryToolProvider ? { enabled: true } : null,
              }
            : {}),
    };
};

export const ProjectDetailsFields = ({ form, readOnly = false }: { form: any; readOnly?: boolean }) => {
    const { t } = useTranslation('chat');
    const errors = form.formState.errors;

    return (
        <>
            <Controller
                name="name"
                control={form.control}
                rules={{ required: t('projects.form.nameRequired') as string }}
                render={({ field }) => (
                    <FormField label={t('projects.form.name')} required error={errors.name}>
                        <StringField
                            {...field}
                            required
                            readOnly={readOnly}
                            placeholder={readOnly ? undefined : t('projects.form.namePlaceholder')}
                        />
                    </FormField>
                )}
            />
            <Controller
                name="description"
                control={form.control}
                render={({ field }) => (
                    <FormField label={t('projects.form.description')} helpText={t('projects.form.descriptionHelp')}>
                        <StringField
                            {...field}
                            readOnly={readOnly}
                            placeholder={readOnly ? undefined : t('projects.form.descriptionPlaceholder')}
                        />
                    </FormField>
                )}
            />
        </>
    );
};

export const ProjectInstructionsFields = ({ form, readOnly = false }: { form: any; readOnly?: boolean }) => {
    const { t } = useTranslation('chat');
    const sections: InstructionSection[] = form.watch('instructions') || [];

    if (readOnly) {
        if (sections.length === 0) {
            return <p className="m-0 text-sm text-content-weak">{t('projects.instructions.empty')}</p>;
        }
        return (
            <div className="flex flex-col gap-4">
                {sections.map((section, index) => (
                    <div key={index} className="flex flex-col gap-1">
                        <div className="text-sm font-medium text-content-strong">{section.label}</div>
                        <p className="m-0 whitespace-pre-wrap text-sm text-content-medium">{section.body}</p>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <Controller
            name="instructions"
            control={form.control}
            render={({ field }) => (
                <InstructionSections
                    label={t('projects.form.instructions')}
                    value={field.value}
                    onChange={field.onChange}
                />
            )}
        />
    );
};

export const ProjectKnowledgeFields = ({
    form,
    project,
    readOnly = false,
    canEditUrls = false,
    canConfigureSources = false,
    canDeleteFile,
    renderFileDetails,
}: {
    form: any;
    // the saved project, for the sync state of its sources (absent while creating)
    project?: any;
    readOnly?: boolean;
    canEditUrls?: boolean;
    canConfigureSources?: boolean;
    canDeleteFile?: (file: any) => boolean;
    renderFileDetails?: (file: any) => React.ReactNode;
}) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const files = form.watch('files') || [];
    const urls: string[] = form.watch('knowledgeUrls') || [];

    const addFiles = (newFiles: File[]) =>
        form.setValue('files', [...(form.getValues('files') || []), ...newFiles], { shouldDirty: true });
    const removeFile = (file: any) =>
        form.setValue(
            'files',
            (form.getValues('files') || []).filter((candidate) => candidate !== file),
            { shouldDirty: true }
        );
    const handleDropRejected = (rejections: any[]) =>
        publishToast({
            description: t('projects.knowledge.unsupported', {
                files: rejections.map((rejection) => rejection.file.name).join(', '),
                supportedTypes: KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL,
            }),
            status: 'error',
        });

    // same order as the assistant's Knowledge page: connections, then the knowledge list with files and web pages
    return (
        <>
            <ProjectKnowledgeConnections
                form={form}
                canEditUrls={canEditUrls}
                canConfigureSources={canConfigureSources}
            />
            <FormField
                label={t('projects.knowledge.files')}
                helpText={
                    readOnly
                        ? undefined
                        : t('projects.knowledge.filesHelp', { supportedTypes: KNOWLEDGE_SUPPORTED_EXTENSIONS_LABEL })
                }
            >
                <div className="flex flex-col gap-2">
                    {!readOnly && (
                        <DropArea
                            placeholder={t('projects.knowledge.drop')}
                            placeholderActive={t('projects.knowledge.dropActive')}
                            onFilesChange={addFiles}
                            onDropRejected={handleDropRejected}
                            accept={KNOWLEDGE_FILE_ACCEPT}
                            files={[]}
                        />
                    )}
                    {files.length > 0 && (
                        <KnowledgeFileList
                            files={files}
                            onDelete={removeFile}
                            canDelete={readOnly ? () => false : canDeleteFile}
                            renderDetails={renderFileDetails}
                        />
                    )}
                    <ProjectKnowledgeUrls form={form} project={project} canEditUrls={canEditUrls} />
                    {readOnly && files.length === 0 && urls.length === 0 && (
                        <span className="text-sm text-content-weak">{t('projects.knowledge.empty')}</span>
                    )}
                </div>
            </FormField>
        </>
    );
};
